import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { acquisitionsApi, type PurchaseOrder } from "@/lib/api/acquisitions";
import { poStatusMap } from "./PurchaseOrdersPage";
import { PackageCheck, Loader2 } from "lucide-react";

/**
 * POs that still need receiving work (sent to vendor, not fully received).
 */
export default function ReceivingDashboardPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sentRes, partialRes] = await Promise.all([
        acquisitionsApi.orders.list({ status: "SENT", limit: 100 }),
        acquisitionsApi.orders.list({ status: "PARTIALLY_RECEIVED", limit: 100 }),
      ]);
      const byId = new Map<string, PurchaseOrder>();
      for (const o of [...sentRes.orders, ...partialRes.orders]) {
        byId.set(o.id, o);
      }
      setOrders(
        Array.from(byId.values()).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    } catch (err) {
      console.error("Failed to load orders for receiving", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      id: "orderNumber",
      header: "PO",
      cell: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/acquisitions/orders/${row.id}`)}
          className="font-mono font-medium text-primary hover:underline"
        >
          {row.orderNumber}
        </button>
      ),
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: (row) => <span className="text-sm text-slate-700">{row.vendor.name}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => {
        const cfg = poStatusMap[row.status] || { variant: "neutral" as const, label: row.status };
        return <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>;
      },
      width: "w-40",
    },
    {
      id: "lines",
      header: "Lines",
      cell: (row) => <span className="tabular-nums text-sm">{row._count?.lines ?? 0}</span>,
      width: "w-16",
      align: "center",
    },
    {
      id: "created",
      header: "Created",
      cell: (row) => (
        <span className="text-xs text-slate-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      id: "action",
      header: "",
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/acquisitions/orders/${row.id}`)}
        >
          <PackageCheck className="mr-2 h-4 w-4" />
          Receive
        </Button>
      ),
      width: "w-32",
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Receiving"
        description="Purchase orders sent to vendors with items still to receive. Open a PO to record a shipment."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Receiving" }]}
        showSearch={false}
        titleSize="md"
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={orders}
          rowKey={(row) => row.id}
          loading={false}
          emptyState={
            <EmptyState
              icon={<PackageCheck className="h-6 w-6 text-slate-400" />}
              title="Nothing to receive"
              description="There are no orders in Sent or Partially Received status, or create and send a purchase order first."
            />
          }
        />
      )}
    </PageContainer>
  );
}
